"use client";

import { useEffect } from "react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  type LucideIcon,
  Search as SearchIcon,
  User,
  ShoppingBag,
  MapPin,
  Heart,
  LayoutGrid,
} from "lucide-react";
import Link from "next/link";
import type { Header as HeaderData } from "@/lib/validations/cms";
import { useCart } from "@/contexts/CartContext";
import { authClient } from "@/lib/auth/client";
import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock";
import { CART_OPEN_EVENT } from "./LiveCart";
import { categorySlug } from "@/lib/shared/utils";
import EditableText from "@/components/edit-mode/EditableText";

const FALLBACK_TAGLINE = "Discover the range.";

type IconName = "user" | "bag" | "track" | "search" | "heart" | "menu";

const UTILITY_ICON_MAP: Record<IconName, LucideIcon> = {
  user: User,
  bag: ShoppingBag,
  track: MapPin,
  search: SearchIcon,
  heart: Heart,
  menu: ArrowUpRight,
};

interface HeaderMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenSearch: () => void;
  data: HeaderData;
  topOffset: number;
}

function slugify(category: string) {
  return categorySlug(category);
}

export default function HeaderMenu({
  open,
  onOpenChange,
  onOpenSearch,
  data,
  topOffset,
}: HeaderMenuProps) {
  const reducedMotion = useReducedMotion();
  const { cart } = useCart();
  const { data: session } = authClient.useSession();
  const user = session?.user;
  const itemCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const userLabel = user ? "Account" : data.accountText;

  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onOpenChange]);

  useBodyScrollLock(open);

  return (
    <AnimatePresence>
      {open ? (
        <m.div
          key="header-menu"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{ top: topOffset }}
          className="bg-background text-foreground fixed inset-x-0 bottom-0 z-[100] flex flex-col border-t"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <nav className="relative z-10 flex-1 overflow-y-auto px-4 pb-16 sm:px-6 lg:px-10">
            <div className="bg-background border-foreground/10 sticky top-0 z-20 -mx-4 mb-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-b px-4 py-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onOpenSearch();
                }}
                className="group/utility text-foreground hover:text-foreground/60 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap transition-colors"
              >
                <SearchIcon size={16} strokeWidth={1.5} aria-hidden />
                <span className="font-display text-sm font-extrabold tracking-[-0.02em] uppercase">
                  <EditableText section="header" field="searchText" value={data.searchText} />
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  window.dispatchEvent(new CustomEvent(CART_OPEN_EVENT));
                }}
                className="group/utility text-foreground hover:text-foreground/60 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap transition-colors"
              >
                <ShoppingBag size={16} strokeWidth={1.5} aria-hidden />
                <span className="font-display text-sm font-extrabold tracking-[-0.02em] uppercase">
                  <EditableText
                    section="cart"
                    field="title"
                    value={itemCount > 0 ? `Bag (${itemCount})` : "Bag"}
                  />
                </span>
              </button>

              {data.utilityLinks.map((link, index) => {
                const Icon = UTILITY_ICON_MAP[link.icon as IconName] ?? ArrowUpRight;
                const href = link.href === "/account" ? (user ? "/account" : "/login") : link.href;
                const label = link.href === "/account" ? userLabel : link.label;

                return (
                  <Link
                    key={`${link.href}-${index}`}
                    href={href}
                    onClick={() => onOpenChange(false)}
                    className="group/utility text-foreground hover:text-foreground/60 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap transition-colors"
                  >
                    <Icon size={16} strokeWidth={1.5} aria-hidden />
                    <span className="font-display text-sm font-extrabold tracking-[-0.02em] uppercase">
                      <EditableText
                        section="header"
                        field={`utilityLinks.${index}.label`}
                        value={label}
                      />
                    </span>
                  </Link>
                );
              })}
            </div>

            <m.p
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-foreground/40 mb-8 font-mono text-[10px] font-bold tracking-[0.3em] uppercase"
            >
              ↓ The Collection
            </m.p>

            <ul className="grid grid-cols-1 gap-x-10 gap-y-0 md:grid-cols-2">
              {data.categories.map((category, idx) => {
                const slug = slugify(category);
                const tagline = data.categoryDescriptions?.[slug] ?? FALLBACK_TAGLINE;
                const number = String(idx + 1).padStart(2, "0");
                return (
                  <m.li
                    key={category}
                    initial={reducedMotion ? false : { opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.1 + idx * 0.05,
                      duration: 0.55,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group/cat border-foreground/10 relative min-w-0 border-b py-10 last:border-b-0 md:[&:nth-last-child(-n+1)]:border-b-0"
                  >
                    <span className="group/cat block min-w-0">
                      <Link
                        href={`/products/category/${slug}`}
                        onClick={() => onOpenChange(false)}
                        className="block min-w-0"
                      >
                        <div className="flex min-w-0 items-baseline gap-3">
                          <span
                            aria-hidden
                            className="text-foreground/30 group-hover/cat:text-foreground/60 shrink-0 font-mono text-xs font-bold tracking-[0.2em] uppercase transition-colors"
                          >
                            {number}
                          </span>
                          <h2 className="font-display text-foreground min-w-0 flex-1 text-2xl leading-[1] font-extrabold tracking-[-0.02em] uppercase transition-[letter-spacing] duration-500 group-hover/cat:tracking-[0em] sm:text-4xl md:text-5xl">
                            <EditableText section="header" field="categories" value={category} />
                          </h2>
                          <ArrowUpRight
                            aria-hidden
                            className="text-foreground/30 group-hover/cat:text-foreground ml-auto size-5 shrink-0 transition-all group-hover/cat:translate-x-1 group-hover/cat:-translate-y-1"
                            strokeWidth={1.5}
                          />
                        </div>
                        <p className="text-foreground/50 mt-3 pl-9 text-sm leading-snug tracking-wide">
                          <EditableText
                            section="header"
                            field={`categoryDescriptions.${slug}`}
                            value={tagline}
                          />
                        </p>
                      </Link>
                    </span>
                  </m.li>
                );
              })}
            </ul>

            <m.div
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + data.categories.length * 0.05 + 0.05, duration: 0.5 }}
              className="mt-10 border-t pt-10"
            >
              <Link
                href="/drops"
                onClick={() => onOpenChange(false)}
                className="group/cat border-foreground/10 relative block min-w-0 border-b py-10"
              >
                <div className="flex min-w-0 items-baseline gap-3">
                  <span
                    aria-hidden
                    className="text-foreground/30 group-hover/cat:text-foreground/60 shrink-0 font-mono text-xs font-bold tracking-[0.2em] uppercase transition-colors"
                  >
                    ★
                  </span>
                  <h2 className="font-display text-foreground min-w-0 flex-1 text-2xl leading-[1] font-extrabold tracking-[-0.02em] uppercase transition-[letter-spacing] duration-500 group-hover/cat:tracking-[0em] sm:text-4xl md:text-5xl">
                    Drops
                  </h2>
                  <ArrowUpRight
                    aria-hidden
                    className="text-foreground/30 group-hover/cat:text-foreground ml-auto size-5 shrink-0 transition-all group-hover/cat:translate-x-1 group-hover/cat:-translate-y-1"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-foreground/50 mt-3 pl-9 text-sm leading-snug tracking-wide">
                  Browse all product drops
                </p>
              </Link>
            </m.div>

            <m.div
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + data.categories.length * 0.05 + 0.08, duration: 0.5 }}
              className="mt-0"
            >
              <Link
                href="/products"
                onClick={() => onOpenChange(false)}
                className="group/cat border-foreground/10 relative block min-w-0 border-b py-10"
              >
                <div className="flex min-w-0 items-baseline gap-3">
                  <span
                    aria-hidden
                    className="text-foreground/30 group-hover/cat:text-foreground/60 shrink-0 font-mono text-xs font-bold tracking-[0.2em] uppercase transition-colors"
                  >
                    <LayoutGrid size={14} strokeWidth={2} />
                  </span>
                  <h2 className="font-display text-foreground min-w-0 flex-1 text-2xl leading-[1] font-extrabold tracking-[-0.02em] uppercase transition-[letter-spacing] duration-500 group-hover/cat:tracking-[0em] sm:text-4xl md:text-5xl">
                    All
                  </h2>
                  <ArrowUpRight
                    aria-hidden
                    className="text-foreground/30 group-hover/cat:text-foreground ml-auto size-5 shrink-0 transition-all group-hover/cat:translate-x-1 group-hover/cat:-translate-y-1"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-foreground/50 mt-3 pl-9 text-sm leading-snug tracking-wide">
                  Browse the full catalog
                </p>
              </Link>
            </m.div>
          </nav>

          <div className="border-foreground/10 relative z-10 flex flex-col gap-2 border-t px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-10">
            <span className="text-foreground/50 font-mono text-[10px] font-bold tracking-[0.25em] uppercase">
              <EditableText section="header" field="copyrightText" value={data.copyrightText} />
            </span>
            <span className="text-foreground/50 hidden font-mono text-[10px] font-bold tracking-[0.25em] uppercase sm:inline">
              Press ESC to close
            </span>
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}

interface MenuToggleProps {
  open: boolean;
  onToggle: () => void;
  compact: boolean;
  label: string;
}

export function MenuToggle({ open, onToggle, compact, label }: MenuToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? "Close menu" : "Open menu"}
      aria-expanded={open}
      className="text-foreground hover:text-foreground/60 group -ml-2 inline-flex cursor-pointer items-center gap-2 px-2 py-2 transition-colors"
    >
      <span aria-hidden className="relative flex w-5 flex-col gap-[3px]">
        <span
          className={`block h-px w-full bg-current transition-transform duration-300 ${
            open ? "translate-y-[3.5px] rotate-45" : ""
          }`}
        />
        <span
          className={`block h-px w-full bg-current transition-opacity duration-200 ${
            open ? "opacity-0" : "opacity-100"
          }`}
        />
        <span
          className={`block h-px w-full bg-current transition-transform duration-300 ${
            open ? "-translate-y-[3.5px] -rotate-45" : ""
          }`}
        />
      </span>
      {!compact ? (
        <span className="hidden font-mono text-[10px] font-bold tracking-[0.2em] uppercase sm:inline">
          <EditableText section="header" field="menuText" value={open ? "Close" : label} />
        </span>
      ) : null}
    </button>
  );
}
